import { z } from 'zod';

/**
 * All user roles (see `docs/10-glossary.md` and the `users` table comment in
 * `docs/03-data-model.md`). Stored per user as a JSON array in the `roles` column.
 */
export const ROLES = [
  'ADMIN',
  'DEVELOPER',
  // M17 (11.08.2026 review, row 4): one editor role, not two. `SALE_EDITOR` and
  // `STOCK_EDITOR` described the same person — "the salesperson takes it from stock and puts
  // it here, same job" — and EDITOR carries the union of what they could each do. Migration
  // 0032 remapped every account; the old values are gone rather than deprecated, because
  // nothing carries them any more (unlike CLIENT below, which accounts still hold).
  'EDITOR',
  'LOGISTIC_EDITOR',
  'WEB_EDITOR',
  'FIRM',
  'FIRM_AUCTION',
  'FIRM_USER',
  // `FIRM_STOCK` was dropped in the same pass — "we don't own the stock". It was
  // permission-identical to `FIRM_VENDOR`, so 0032 remapped its accounts there and nobody
  // gained or lost access.
  'FIRM_VENDOR',
  'GOLDBIDDER',
  'BIDDER',
  'USER',
  'ANONYMOUS',
  // DEPRECATED (M13, ADR 0026). The role the M4.6 per-account ingestion keys hung off.
  // Ingestion is now authenticated by a per-FIRM API key that resolves to a `companies` row,
  // so nothing consults this role any more. Kept only so accounts already tagged with it stay
  // valid; it grants nothing. A partner firm's people belong in the `FIRM_*` roles.
  'CLIENT',
] as const;

export const roleSchema = z.enum(ROLES);
export type Role = z.infer<typeof roleSchema>;

/** Roles allowed to manage users (M1 admin CRUD). Broader admin surfaces exist per endpoint. */
export const USER_ADMIN_ROLES = ['ADMIN', 'DEVELOPER'] as const satisfies readonly Role[];
